import React from 'react';
import {
  Box,
  Button,
  Flex,
  Tab,
  TabList,
  Tabs,
  Link as ChakraLink,
} from '@chakra-ui/react';
import {Link, Navigate, Route, Routes, useMatch} from 'react-router-dom';
import AccountGrid from '../AccountGrid';
import {TabbedPage} from '../TabbedPage';
import {
  useIsFeatureEnabled,
  useIsFreemiumV3Customer,
  useIsFreeOrListBuilderCustomer,
  useIsListBuilderCustomer,
  useMarkets,
} from '../../hooks/api/metadata';
import _ from 'lodash';
import {useIntegrations} from '../../hooks/api/integrations';
import {FreemiumV3EmptyState} from './FreemiumV3EmptyState';
import {useScoringRunCounts} from '../../hooks/api/scoringRunCounts';
import {pluralize} from '../../lib/helpers';

const MyAccounts = () => {
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();
  const isFreemiumV3 = useIsFreemiumV3Customer();

  return isFreemiumV3 ? (
    <FreemiumV3MyAccounts />
  ) : isFreeOrListBuilderCustomer ? (
    <ListBuilderMyAccounts />
  ) : (
    <PaidMyAccounts />
  );
};

const FreemiumV3MyAccounts = () => {
  const {numActive} = useScoringRunCounts();

  if (!numActive) {
    return <FreemiumV3EmptyState />;
  }

  return (
    <Flex direction="column" h="100%">
      <Flex
        alignItems="baseline"
        bgColor="kgray.100"
        borderBottom="1px solid"
        borderBottomColor="kgray.200"
        height="100px"
        p={5}
      >
        <Box visibility={numActive !== undefined ? 'visible' : 'hidden'}>
          <Box fontSize="2xl" mb={2}>
            Showing{' '}
            {pluralize(numActive ?? 0, 'engaged account', 'engaged accounts')}
          </Box>
          <Box color="kgray.300" display={{base: 'none', lg: 'block'}}>
            Filter & export your accounts here.{' '}
            <ChakraLink
              color="kblue.300"
              href="https://keyplay.io/pricing"
              target="_blank"
            >
              Upgrade
            </ChakraLink>{' '}
            for full coverage.
          </Box>
        </Box>
      </Flex>
      <Routes>
        <Route path="intent" element={<AccountGrid gridView="intent" />} />
        <Route path="*" element={<Navigate to="/accounts/intent" replace />} />
      </Routes>
    </Flex>
  );
};

const ListBuilderMyAccounts = () => {
  const isListBuilder = useIsListBuilderCustomer();
  const {data, isPending, isError} = useIntegrations({});
  const markets = useMarkets();
  const {numActive} = useScoringRunCounts();

  const integrationsLoading = isPending || isError;
  const hubspotConnected =
    data?.find(({type}) => type === 'hubspot')?.status === 'active';

  return (
    <Flex direction="column" h="100%">
      <Flex
        alignItems="baseline"
        bgColor="kgray.100"
        height="140px"
        gap={2}
        px={5}
        py={8}
        justifyContent="space-between"
      >
        <Box visibility={numActive !== undefined ? 'visible' : 'hidden'}>
          <Box fontSize="2xl" mb={2}>
            You&apos;re tracking{' '}
            {pluralize(numActive ?? 0, 'saved account', 'saved accounts')}
          </Box>
          <Box color="kgray.300" display={{base: 'none', lg: 'block'}}>
            Filter & export your accounts here.
          </Box>
        </Box>
        <Flex gap="2" direction={{base: 'column', lg: 'row'}}>
          {isListBuilder && !integrationsLoading && !hubspotConnected && (
            <Button
              as={Link}
              colorScheme="kbuttonblue"
              flexShrink="0"
              fontWeight="normal"
              to="/settings/integrations"
              variant="outline"
            >
              Connect HubSpot
            </Button>
          )}
          <Button
            as={Link}
            colorScheme="kbuttonblue"
            flexShrink="0"
            fontWeight="normal"
            to={`/markets/${markets[0].id}`}
          >
            View & Adjust Scoring
          </Button>
        </Flex>
      </Flex>
      <Routes>
        <Route path="saved" element={<AccountGrid gridView="saved" />} />
        <Route path="*" element={<Navigate to="/accounts/saved" />} />
      </Routes>
    </Flex>
  );
};

const PaidMyAccounts = () => {
  const isAllTab = useMatch('/accounts/all');
  const isCrmTab = useMatch('/accounts/crm');
  const isSavedTab = useMatch('/accounts/saved');
  const isIntentTab = useMatch('/accounts/intent');
  const isImportedTab = useMatch('/accounts/imported');

  const showImportsTab = useIsFeatureEnabled('importsTab');
  const showIntentTab = useIsFeatureEnabled('intent');

  return (
    <TabbedPage title="Accounts">
      <Tabs
        variant="line"
        index={_.findIndex(
          [
            isAllTab,
            isCrmTab,
            isSavedTab,
            ...[showIntentTab ? isIntentTab : []],
            ...[showImportsTab ? isImportedTab : []],
          ],
          (x) => !!x
        )}
        display="flex"
        flexDir="column"
        h="100%"
      >
        <TabList>
          <Tab as={Link} to="all" ml={24}>
            All
          </Tab>
          <Tab as={Link} to="crm">
            CRM
          </Tab>
          <Tab as={Link} to="saved">
            Saved
          </Tab>
          {showIntentTab && (
            <Tab as={Link} to="intent">
              Intent
            </Tab>
          )}
          {showImportsTab && (
            <Tab as={Link} to="imported">
              Imported
            </Tab>
          )}
        </TabList>

        <Routes>
          <Route path="all" element={<AccountGrid gridView="all" />} />
          {showImportsTab && (
            <Route
              path="imported"
              element={<AccountGrid gridView="imported" />}
            />
          )}
          {showIntentTab && (
            <Route path="intent" element={<AccountGrid gridView="intent" />} />
          )}
          <Route path="saved" element={<AccountGrid gridView="saved" />} />
          <Route path="crm" element={<AccountGrid gridView="crm" />} />
          <Route path="*" element={<Navigate to="/accounts/all" />} />
        </Routes>
      </Tabs>
    </TabbedPage>
  );
};

export default MyAccounts;
